// ═══════════════════════════════════════════════════════════════════════════
// MFAGate — Bloquea la app hasta que el consultor enrolle segundo factor
// SEC-B3 (mayo 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// USO
// ───
//   <ConsultorRoute>
//     <MFAGate>
//       <Layout />
//     </MFAGate>
//   </ConsultorRoute>
//
// LÓGICA
// ──────
//   1. Lee `useMFAStatus()`:
//        - required:  ¿claim mfaRequired:true presente?
//        - enrolled:  ¿tiene segundo factor activo?
//   2. Si `loading` → spinner.
//   3. Si NO required → renderiza children sin tocar (default: clientes y
//      consultores pre-claim).
//   4. Si required && enrolled → renderiza children. El login ya pasó por
//      segundo factor (lo verifica el backend con requireMFA).
//   5. Si required && !enrolled → renderiza pantalla fullscreen con
//      `<MFASection forced />` + logout escape hatch. El usuario NO puede
//      navegar a ninguna ruta hasta enrollar.
//
// IMPORTANTE: El gate NO bloquea por "completed" (si el sign-in actual pasó
// por segundo factor). Eso lo valida el backend a nivel callable con
// `requireMFA(request)`. Si la sesión es antigua (sin sign_in_second_factor)
// y el usuario intenta una acción admin, el backend tira mfa_required y el
// frontend captura el error y le pide re-login. Mantener esa lógica en el
// backend evita race conditions con el refresh del token.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect } from 'react'
import { signOut } from 'firebase/auth'
import { auth } from '@core/lib/firebase'
import { useMFAStatus } from '@core/hooks/useMFAStatus'
import MFASection from '@core/components/MFASection'

function FullSpinner() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-brand-bg z-50">
      <div className="w-9 h-9 border-[3px] border-transparent border-t-brand-accent rounded-full animate-spin" />
    </div>
  )
}

function IconLogout() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

export default function MFAGate({ children }) {
  const { required, enrolled, loading, error, refresh } = useMFAStatus()

  // Cuando el usuario termina de enrollar, MFASection llama onEnrolled que
  // dispara este refresh. El token se actualiza con los factores nuevos.
  const handleEnrolled = async () => {
    await refresh()
  }

  // Si en cualquier render el usuario quedó con required && enrolled, hacemos
  // un refresh extra por las dudas (corner case: el claim cambió post-mount).
  useEffect(() => {
    if (required && enrolled) {
      // ya pasó el gate, nada que hacer
    }
  }, [required, enrolled])

  if (loading) return <FullSpinner />

  // No se pudo leer el token (dos intentos, o colgado): se bloquea con salida.
  // Antes quedaba `required: false` y se dejaba pasar (fail-open, 24-sep-2026).
  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6">
        <div className="max-w-md w-full p-6 rounded-xl bg-white/[0.03] border border-white/[0.08]">
          <h1 className="text-[18px] font-semibold text-white mb-2">No pudimos verificar tu seguridad</h1>
          <p className="text-[13px] text-zinc-400 leading-relaxed mb-5">
            No se pudo leer tu sesión (suele ser la conexión). Reintentá; si sigue, cerrá sesión y volvé a entrar.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => refresh()}
              className="text-[13px] font-semibold px-4 py-2 rounded-lg bg-brand-accent text-white hover:opacity-90"
            >
              Reintentar
            </button>
            <button
              onClick={() => signOut(auth)}
              className="text-[13px] px-4 py-2 rounded-lg border border-white/[0.08] text-zinc-300 hover:bg-white/[0.04]"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Cliente o consultor sin política activa → passthrough total.
  if (!required) return children

  // Política activa Y factor ya enrollado → passthrough. El backend valida
  // sesión-por-sesión con requireMFA (responde mfa_required si el sign-in
  // actual no pasó por segundo factor).
  if (enrolled) return children

  // Política activa Y sin factor → bloqueo fullscreen.
  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <header className="border-b border-white/[0.06] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-300 flex items-center justify-center text-sm font-bold">
            !
          </div>
          <div>
            <div className="text-[14px] font-semibold text-white">Configuración de seguridad pendiente</div>
            <div className="text-[12px] text-zinc-400">{auth.currentUser?.email}</div>
          </div>
        </div>
        <button
          onClick={() => signOut(auth)}
          className="inline-flex items-center gap-2 text-[12px] text-zinc-400 hover:text-white px-3 py-1.5 rounded-lg border border-white/[0.08] hover:bg-white/[0.04]"
        >
          <IconLogout />
          Cerrar sesión
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-[28px] font-bold text-white mb-2">
            Activá la autenticación de dos factores
          </h1>
          <p className="text-[14px] text-zinc-400 leading-relaxed">
            Como consultor de Milbrands tenés acceso a operaciones críticas — kill switches del
            sistema, gestión de paquetes, credenciales de IA, exportaciones GDPR. Por política
            de seguridad, todas las cuentas del <strong className="text-white">equipo de Milbrands</strong> necesitan
            tener un segundo factor activo antes de operar.
          </p>
          <p className="text-[14px] text-zinc-400 leading-relaxed mt-2">
            Te toma menos de 2 minutos. Necesitás una app de códigos en tu celular (Google
            Authenticator, Authy, Microsoft Authenticator o 1Password).
          </p>

          <MFASection forced onEnrolled={handleEnrolled} />

          <div className="mt-8 p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
            <div className="text-[12px] font-semibold text-blue-300 mb-1">¿Algún problema?</div>
            <p className="text-[12px] text-zinc-400 leading-relaxed">
              Si no podés activar 2FA en este momento, cerrá sesión y contactá a un super-admin
              para que te ayude. Tu cuenta sigue activa, solo te faltará este último paso para
              poder operar.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
